import PropTypes from 'prop-types'
import { ArrowsRightLeftIcon } from '@heroicons/react/24/solid'
import React, { useEffect, useState } from 'react'

import { Modal } from '../../components/Modal'
import { getAccountMergePreview, mergeClientAccounts } from '../../services/accounts.service'
import { toast } from '../../utils/helpers'

const MergeAccountsModal = ({ clientId, source, target, closeModal, onSuccess }) => {
  const [loadingPreview, setLoadingPreview] = useState(true)
  const [loadingMerge, setLoadingMerge] = useState(false)
  const [preview, setPreview] = useState(null)

  const handleError = (m) => toast(m, 'error')
  const handleSuccess = (m) => toast(m, 'success')

  useEffect(() => {
    const loadPreview = async () => {
      const response = await getAccountMergePreview(
        clientId,
        source.id,
        target.id,
        handleError,
        setLoadingPreview,
      )
      if (response) setPreview(response)
    }
    loadPreview()
  }, [clientId, source.id, target.id])

  const onConfirm = async () => {
    const response = await mergeClientAccounts(
      clientId,
      source.id,
      target.id,
      handleError,
      setLoadingMerge,
      handleSuccess,
    )
    if (response) onSuccess()
  }

  // `conflicts` (the overlapping periods) is the authoritative signal for whether a merge is
  // refused — mirror the API contract rather than leaning on the secondary `skipCount`.
  const conflicts = preview?.conflicts ?? []
  const hasConflicts = conflicts.length > 0
  // Block merging until we've confirmed there are no overlapping periods. The user resolves the
  // overlap first; the API refuses the merge as a backstop.
  const mergeDisabled = loadingPreview || !preview || hasConflicts

  return (
    <Modal
      icon={<ArrowsRightLeftIcon className="h-6 fill-white" />}
      open
      title="Merge Duplicate Accounts"
      loading={loadingMerge}
      onClose={closeModal}
      content={
        <div className="mt-6 flex flex-col gap-4">
          <p className="text-sm text-gray-700">
            An account with the name <span className="font-semibold">{target.name}</span> and
            number <span className="font-semibold">{target.number}</span> already exists. Merging
            moves all portfolio data from the account you just edited into the existing account.
          </p>

          {loadingPreview && <p className="text-sm text-gray-500">Calculating impact...</p>}

          {!loadingPreview && preview && !hasConflicts && (
            <div className="rounded border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
              <span className="font-semibold">{preview.reassignCount}</span> portfolio data row
              {preview.reassignCount === 1 ? '' : 's'} will be moved to the target.
            </div>
          )}

          {!loadingPreview && hasConflicts && (
            <div className="rounded border-l-4 border-amber-400 bg-amber-50 p-3 text-sm text-amber-900">
              <p>
                These accounts both have portfolio data for the same period
                {conflicts.length === 1 ? '' : 's'}, so they can&rsquo;t be merged automatically.
                Resolve the overlapping row
                {conflicts.length === 1 ? '' : 's'} below, then try again.
              </p>
              <ul className="mt-2 list-disc space-y-0.5 pl-5">
                {conflicts.map((c) => (
                  <li key={`${c.composite ?? ''}-${c.portfolio ?? ''}-${c.period}`}>
                    {c.composite || 'No composite'}
                    {c.portfolio ? ` · ${c.portfolio}` : ''} — {c.period}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      }
      actions={[
        {
          type: 'submit',
          label: 'Merge Accounts',
          onClick: onConfirm,
          disabled: mergeDisabled,
        },
        { type: 'cancel', label: 'Cancel', onClick: closeModal },
      ]}
    />
  )
}

MergeAccountsModal.propTypes = {
  clientId: PropTypes.string.isRequired,
  source: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    number: PropTypes.string.isRequired,
  }).isRequired,
  target: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    number: PropTypes.string.isRequired,
  }).isRequired,
  closeModal: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
}

export default MergeAccountsModal
